import React, { useEffect, useState, useCallback } from "react";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError } from "../utils/errorHandler";
import {
  Button,
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
  Alert,
  Popover,
  MenuItem,
  Menu,
  Switch,
  FormControl,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DocumentsPanel = () => {
  const [form, setForm] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    doc_id: "",
    name: "",
    link: "",
    active_flag: 1,
    attachment: null,
  });

  // Track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState({});

  // NEW: Track the name of the currently attached file when editing
  const [existingAttachmentName, setExistingAttachmentName] = useState(null);

  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    environment_name: true,
    application_name: true,
    name: true,
    doc_id: true,
    link: true,
    attachment: true,
  });

  const [filters, setFilters] = useState({
    client_name: "",
    environment_name: "",
    application_name: "",
    doc_id: "",
    name: "",
    link: "",
    attachment: "",
  });

  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState(null);
  const [total, setTotal] = useState(0);

  // Fetch documents with backend pagination
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    // Add search parameter if present
    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm);
    }

    // Add filters to params
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key].trim() !== "") {
        params.append(key, filters[key]);
      }
    });

    try {
      const result = await apiService.get(
        `/documents/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error fetching documents:", err.message);
      setData([]);
      setTotal(0);
      setError("Failed to fetch documents. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filters]);

  // Fetch initial data only once on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [clientsData, envData, appsData] = await Promise.all([
          apiService.get("/clients"),
          apiService.get("/environments"),
          apiService.get("/applications"),
        ]);
        setClients(clientsData);
        setEnvironments(envData);
        setApplications(appsData);
      } catch (err) {
        console.error("Error fetching initial data:", err);
        setError("Failed to load initial data. Please try again.");
      }
    };
    fetchInitialData();
  }, []); // Only run once on mount

  // Fetch documents when dependencies change
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]); // Fetch when page, rowsPerPage, or searchTerm changes

  useEffect(() => {
    if (clients.length > 0 && !form.cl_id) {
      setForm((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients, form.cl_id]);

  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    doc_id: "Document ID",
    name: "Document",
    link: "Link",
    attachment: "Attachment",
  };

  const validate = () => {
    const errors = {};
    const onlySpecialCharRegex = /^[^a-zA-Z0-9]+$/; // Only special characters
    const onlyNumbersRegex = /^[0-9]+$/; // Only numbers
    const noLettersRegex = /^[^a-zA-Z]+$/; // No letters (only numbers and/or special chars)
    
    // Environment validation
    if (!form.env_id || !form.env_id.toString().trim()) {
      errors.env_id = "Environment is required.";
    }
    
    // Application validation
    if (!form.app_id || !form.app_id.toString().trim()) {
      errors.app_id = "Application is required.";
    }
    
    // Document ID validation
    const trimmedDocId = form.doc_id?.toString().trim();
    if (!trimmedDocId) {
      errors.doc_id = "Document ID is required.";
    } else if (onlySpecialCharRegex.test(trimmedDocId)) {
      errors.doc_id = "Document ID cannot contain only special characters.";
    } else if (onlyNumbersRegex.test(trimmedDocId)) {
      errors.doc_id = "Document ID cannot contain only numbers.";
    } else if (noLettersRegex.test(trimmedDocId)) {
      errors.doc_id = "Document ID must contain at least one letter.";
    } else if (!isEditing) {
      // Check for duplicate Document ID only when adding new
      const isDuplicate = data.some(
        (doc) => doc.doc_id?.toString().trim().toLowerCase() === trimmedDocId.toLowerCase()
      );
      if (isDuplicate) {
        errors.doc_id = "Document ID already exists.";
      }
    }
    
    // Name validation
    const trimmedName = form.name?.trim();
    if (!trimmedName) {
      errors.name = "Name is required.";
    } else if (onlySpecialCharRegex.test(trimmedName)) {
      errors.name = "Name cannot contain only special characters.";
    } else if (onlyNumbersRegex.test(trimmedName)) {
      errors.name = "Name cannot contain only numbers.";
    } else if (noLettersRegex.test(trimmedName)) {
      errors.name = "Name must contain at least one letter.";
    }
    
    // Link validation
    const trimmedLink = form.link?.trim();
    if (!trimmedLink) {
      errors.link = "Link is required.";
    }
    
    return errors;
  };

  // Check if form has changed
  const hasFormChanged = () => {
    if (!isEditing) return true; // Always allow submit for new entries
    return JSON.stringify(form) !== JSON.stringify(originalFormData);
  };

  const fetchApplicationsByEnvironment = async (env_id) => {
    if (!env_id) {
      setFilteredApplications([]);
      return;
    }

    try {
      const response = await apiService.get(
        `/documents/applications/by-environment?env_id=${env_id}`,
      );
      setFilteredApplications(response);
    } catch (error) {
      console.error("Error fetching applications by environment:", error);
      setFilteredApplications([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "attachment") {
      setForm({ ...form, attachment: files?.[0] || null });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleEnvironmentChange = async (event, newValue) => {
    const env_id = newValue ? newValue.env_id : "";

    setForm((prev) => ({
      ...prev,
      env_id: env_id,
      app_id: "", // Clear application when environment changes (cascade)
    }));

    setFormErrors((prev) => ({ ...prev, env_id: "", app_id: "" }));

    if (env_id) {
      await fetchApplicationsByEnvironment(env_id);
    } else {
      setFilteredApplications([]);
    }
  };

  const handleApplicationChange = (event, newValue) => {
    setForm((prev) => ({
      ...prev,
      app_id: newValue ? newValue.app_id : "",
    }));
    setFormErrors((prev) => ({ ...prev, app_id: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (isEditing) {
        await apiService.put(`/documents/${form.doc_id}`, {
          name: form.name.trim(),
          link: form.link.trim(),
          active_flag: form.active_flag,
        });
      } else {
        const formData = new FormData();
        formData.append("cl_id", form.cl_id);
        formData.append("env_id", form.env_id);
        formData.append("app_id", form.app_id);
        formData.append("doc_id", form.doc_id.toString().trim());
        formData.append("name", form.name.trim());
        formData.append("link", form.link.trim());
        formData.append("active_flag", form.active_flag);

        if (form.attachment) {
          formData.append("attachment", form.attachment);
        }

        await apiService.request("/documents", {
          method: "POST",
          body: formData,
        });
      }

      fetchDocuments();
      setSuccessMessage(
        isEditing
          ? "Document updated successfully!"
          : "Document added successfully!",
      );
      setForm({
        cl_id: "",
        env_id: "",
        app_id: "",
        doc_id: "",
        name: "",
        link: "",
        active_flag: 1,
        attachment: null,
      });
      setExistingAttachmentName(null);
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(""), 3000);
      setOpenDialog(false);
    } catch (err) {
      console.error("Error submitting form:", err);
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(err, "Document")
        : handleCreateError(err, "Document");
      setError(errorMessage);
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleEdit = async (doc) => {
    // Guess filename from document name (common pattern)
    let filename = null;
    if (doc.attachment) {
      // Try to extract extension from name or default to .pdf
      const match = doc.name?.match(/\.(pdf|docx?|txt|jpe?g|png)$/i);
      const ext = match ? match[0] : ".pdf";
      filename = `${doc.name}${ext}`;
    }

    setExistingAttachmentName(filename || null);

    const formData = {
      ...doc,
      attachment: null, // clear any new file selection
      doc_id: doc.doc_id,
      active_flag: doc.active_flag !== undefined ? doc.active_flag : 1,
    };

    setForm(formData);
    setOriginalFormData(formData); // Store original for change detection

    if (doc.env_id) {
      await fetchApplicationsByEnvironment(doc.env_id);
    }

    setFormErrors({});
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDelete = (doc_id) => {
    setDeleteDocId(doc_id);
    setOpenDeleteDialog(true);
  };

  const handleDialogOpen = () => {
    const defaultClient = clients.length > 0 ? clients[0].cl_id : "";
    const defaultClientName = clients.length > 0 ? clients[0].name : "";

    setForm({
      cl_id: defaultClient,
      client_name: defaultClientName,
      env_id: "",
      app_id: "",
      doc_id: "",
      name: "",
      link: "",
      active_flag: 1,
      attachment: null,
    });

    setExistingAttachmentName(null);
    setFilteredApplications([]);
    setFormErrors({});
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setExistingAttachmentName(null);
  };

  const handleDownloadAttachment = async (doc_id, doc_name) => {
    try {
      const response = await fetch(
        `${BASE_URL}/documents/${doc_id}/attachment`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc_name || "document";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      setError("Failed to download attachment. Please try again.");
    }
  };

  const handleToggleFlag = async (doc_id, currentFlag) => {
    try {
      const newFlag = currentFlag === 1 ? 0 : 1;
      await apiService.put(`/documents/${doc_id}`, { active_flag: newFlag });
      fetchDocuments();
      setSuccessMessage(`Document ${newFlag === 1 ? "Activated" : "Deactivated"}`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      setError("Failed to toggle active flag. Please try again.");
      setTimeout(() => setError(""), 3000);
    }
  };

//   const handleToggleFlag = async (doc_id, currentFlag) => {
//   try {
//     const newFlag = currentFlag === 1 ? 0 : 1;

//     await apiService.put(`/documents/${doc_id}`, {
//       active_flag: newFlag,
//     });

//     fetchDocuments();

//     const message =
//       newFlag === 1
//         ? "Document Activated Successfully!"
//         : "Document Deactivated Successfully!";

//     setSuccessMessage(message);
//     setError(null);

//     setTimeout(() => setSuccessMessage(""), 3000);
//   } catch (error) {
//     console.error("Error toggling active flag:", error);
//     setError("Failed to toggle active flag. Please try again.");
//     setSuccessMessage("");

//     setTimeout(() => setError(""), 3000);
//   }
// };
  const columns = [
    "client_name",
    "environment_name",
    "application_name",
    "doc_id",
    "name",
    "link",
    "attachment",
  ];

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const compactMultilineStyles = {
    "& .MuiInputBase-root": {
      minHeight: "80px",
      fontSize: "0.85rem",
      padding: "0 10px",
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
      lineHeight: "1.2",
      overflowY: "auto",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      top: "-2px",
    },
    "& .MuiInputLabel-shrink": {
      top: "0",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.875rem",
    },
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Documents
        </Typography>

        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1); // Reset to first page when searching
            }}
            sx={{
              width: "200px",
              mr: 1,
              "& .MuiInputBase-root": { height: 35 },
              "& .MuiInputBase-input": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
            InputLabelProps={{ shrink: true }}
          />

          <Button
            onClick={handleDialogOpen}
            sx={{
              color: "#666",
              fontWeight: "bold",
              borderRadius: "8px",
              height: "35px",
              minWidth: "50px",
              px: 1.5,
              textTransform: "none",
              fontSize: "12px",
              "&:hover": { color: "#1976d2" },
            }}
          >
            Add +
          </Button>

          <Tooltip title="Column Customization" arrow>
            <IconButton
              onClick={handleOpen}
              sx={{
                color: "#666",
                height: "35px",
                width: "30px",
                borderRadius: "8px",
                "&:hover": { color: "#1976d2" },
              }}
            >
              <SettingsIcon style={{ width: 17, height: 17 }} />
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={() => page > 1 && setPage(page - 1)}
            disabled={page <= 1}
            size="small"
            sx={{
              width: "30px",
              height: "35px",
              borderRadius: "6px",
              color: "#666",
              "&:hover": { color: "#1976d2" },
              "&:disabled": { color: "#ccc" },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </IconButton>

          <Box
            sx={{
              fontSize: "13px",
              fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, total)} of ${total}`}
          </Box>

          <IconButton
            onClick={() => {
              const totalPages = Math.ceil(total / rowsPerPage);
              if (page < totalPages) setPage(page + 1);
            }}
            disabled={page >= Math.ceil(total / rowsPerPage)}
            size="small"
            sx={{
              width: "30px",
              height: "35px",
              borderRadius: "6px",
              color: "#666",
              "&:hover": { color: "#1976d2" },
              "&:disabled": { color: "#ccc" },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </IconButton>
        </Box>
      </Box>

      {(successMessage || error) && (
        <Alert
          severity={successMessage ? "success" : "error"}
          sx={{ mb: 2 }}
          onClose={() => {
            setSuccessMessage("");
            setError(null);
          }}
        >
          {successMessage || error}
        </Alert>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{ fontSize: "0.8rem", color: "#333", fontWeight: "bold" }}
              >
                {columnLabels[col] || col}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650, tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "120px", minWidth: "120px" }}>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell 
                            key={col}
                            sx={{
                              minWidth: 
                                col === "client_name" ? "120px" :
                                col === "environment_name" ? "140px" :
                                col === "application_name" ? "140px" :
                                col === "doc_id" ? "120px" :
                                col === "name" ? "180px" :
                                col === "link" ? "200px" :
                                col === "attachment" ? "160px" :
                                "100px",
                              paddingLeft: "16px",
                              paddingRight: "16px"
                            }}
                          >
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col]}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((doc) => (
                      <TableRow key={doc.doc_id}>
                        <TableCell>
                          <Box display="flex" gap={1} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(
                                  doc.doc_id,
                                  parseInt(doc.active_flag),
                                )
                              }
                             title="Toggle Active Flag"
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(doc.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  width: 17,
                                  height: 17,
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(doc)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(doc.doc_id)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon style={{ width: 12, height: 15 }} />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {visibleColumns.client_name && (
                          <TableCell>{doc.client_name}</TableCell>
                        )}
                        {visibleColumns.environment_name && (
                          <TableCell>{doc.environment_name}</TableCell>
                        )}
                        {visibleColumns.application_name && (
                          <TableCell>{doc.application_name}</TableCell>
                        )}
                        {visibleColumns.doc_id && (
                          <TableCell sx={{ paddingLeft: "16px", paddingRight: "16px" }}>
                            {doc.doc_id}
                          </TableCell>
                        )}
                        {visibleColumns.name && (
                          <TableCell sx={{ paddingLeft: "16px", paddingRight: "16px" }}>
                            {doc.name}
                          </TableCell>
                        )}
                        {visibleColumns.link && (
                          <TableCell sx={{ 
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}>
                            <Tooltip title={doc.link || ""} arrow>
                              <span>
                                {doc.link && doc.link.length > 50
                                  ? `${doc.link.substring(0, 50)}...`
                                  : doc.link || ""}
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}

                        {visibleColumns.attachment && (
                          <TableCell sx={{ 
                            width: "150px",
                            maxWidth: "150px"
                          }}>
                            {doc.attachment ? (
                              <Button
                                variant="text"
                                size="small"
                                onClick={() =>
                                  handleDownloadAttachment(doc.doc_id, doc.name)
                                }
                                sx={{
                                  color: "blue",
                                  textDecoration: "underline",
                                  textTransform: "none",
                                  minWidth: "auto",
                                  p: 0,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                Download
                              </Button>
                            ) : (
                              "No Document Found"
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 1} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No documents found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>

        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            // onClose={handleDialogClose}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDialog(false);
            }}
            maxWidth="md"
            Width="small"
          >
            <DialogTitle sx={{ fontWeight: "700", fontSize: "16px", py: 1 }}>
              {isEditing ? "Edit Document" : "Add Document"}
              <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2.1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  label="Client"
                  value={
                    form.client_name ||
                    clients.find((c) => c.cl_id === form.cl_id)?.name ||
                    ""
                  }
                  InputProps={{ readOnly: true }}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                      color: "grey",
                    },
                  }}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.env_id}
                >
                  <Autocomplete
                    options={environments}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      environments.find((e) => e.env_id === form.env_id) || null
                    }
                    onChange={handleEnvironmentChange}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Environment"
                        error={!!formErrors.env_id}
                        required
                        helperText={formErrors.env_id}
                      />
                    )}
                    disabled={environments.length === 0}
                  />
                </FormControl>

                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.app_id}
                >
                  <Autocomplete
                    options={filteredApplications}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      filteredApplications.find(
                        (a) => a.app_id === form.app_id,
                      ) || null
                    }
                    onChange={handleApplicationChange}
                    disabled={!form.env_id || filteredApplications.length === 0}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Application"
                        error={!!formErrors.app_id}
                        required
                        helperText={formErrors.app_id}
                      />
                    )}
                  />
                </FormControl>

                <TextField
                  fullWidth
                  label="Document ID"
                  name="doc_id"
                  value={form.doc_id}
                  onChange={handleChange}
                  required
                  error={!!formErrors.doc_id}
                  helperText={formErrors.doc_id}
                  margin="normal"
                  InputProps={{ readOnly: isEditing }}
                  sx={
                    isEditing
                      ? {
                          backgroundColor: "white",
                          "& .MuiInputBase-input": {
                            cursor: "not-allowed",
                            color: "grey",
                          },
                        }
                      : {}
                  }
                />

                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  margin="normal"
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 0.5,
                    mb: 2,
                  }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      px: 2,
                      minWidth: 110,
                      borderColor: "#bdbdbd",
                      color: "#1976d2",
                      fontSize: "0.875rem",
                      "&:hover": {
                        borderColor: "#1976d2",
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    Choose File
                    <input
                      type="file"
                      name="attachment"
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                      hidden
                      onChange={handleChange}
                    />
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    {form.attachment ? form.attachment.name : "No file chosen"}
                  </Typography>

                  {isEditing && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#1976d2",
                        fontWeight: 500,
                      }}
                    >
                      Current file: {existingAttachmentName || "none"}
                    </Typography>
                  )}
                </Box>

                <TextField
                  fullWidth
                  label="Link"
                  name="link"
                  value={form.link}
                  onChange={handleChange}
                  required
                  error={!!formErrors.link}
                  helperText={formErrors.link}
                  margin="normal"
                  // sx={{ gridColumn: "1 / -1" }}
                   sx={{
                                     gridColumn: "span 2", 
                     minWidth: 300, mt: -0.05,
                    ...compactMultilineStyles,
                            width: "100%",
                   }}
                   multiline
                          rows={2}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained"
                disabled={isEditing && !hasFormChanged()}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDeleteDialog(false);
              setDeleteDocId(null);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Document{" "}
                <strong>
                  {data.find((doc) => doc.doc_id === deleteDocId)?.name ||
                    deleteDocId}
                </strong>{" "}
                ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setDeleteDocId(null);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await apiService.delete(`/documents/${deleteDocId}`);
                    setSuccessMessage("Document deleted successfully!");
                    setError(null);
                    fetchDocuments();
                    setTimeout(() => setSuccessMessage(""), 3000);
                  } catch (err) {
                    console.error("Error deleting document:", err);
                    // Use centralized error handler
                    const errorMessage = handleDeleteError(err, "Document");
                    setError(errorMessage);
                    setTimeout(() => setError(""), 5000);
                  } finally {
                    setOpenDeleteDialog(false);
                    setDeleteDocId(null);
                  }
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
};

export default DocumentsPanel;
